import { Card } from "@/components/ui/Card"
import { formatMoneyShort } from "@/lib/money"
import { cn } from "@/lib/utils"
import Figure from "./Figure"
import { percentOf } from "./format"
import type { YearProgressData } from "./types"
import YearProgressChart from "./YearProgressChart"

interface Props {
  currency: string | null
  /** The plan's first and last day (ISO), for the title. */
  planBegin: string
  planEnd: string
  trendDays: number
  year: YearProgressData
}

/** "Full year 2026 Progress" when the plan runs Jan 1 – Dec 31 of one year. */
function title(planBegin: string, planEnd: string): string {
  const year = planBegin.slice(0, 4)
  const fullYear = planBegin === `${year}-01-01` && planEnd === `${year}-12-31`
  return fullYear ? `Full year ${year} Progress` : "Plan period Progress"
}

// The whole plan period: target, actual so far, where it lands at the recent
// pace, and the months behind it.
export default function YearProgress({ currency, planBegin, planEnd, trendDays, year }: Props) {
  const { target, actual, estimate } = year
  const money = (v: number) => formatMoneyShort(v, currency)
  const actualPct = percentOf(actual, target)
  const estimatePct = percentOf(estimate, target)
  const gap = target == null ? null : estimate - target
  return (
    <Card className="overflow-hidden">
      <div className="border-b px-4 py-3">
        <div className="text-sm font-semibold">{title(planBegin, planEnd)}</div>
      </div>
      <div className={cn("grid divide-x border-b", gap == null ? "grid-cols-3" : "grid-cols-4")}>
        <Figure label="Year target" value={target == null ? "-" : money(target)} />
        <Figure label="Actual so far" value={money(actual)}>
          {actualPct != null && <div>{actualPct}% of target</div>}
        </Figure>
        <Figure label="Latest estimate" value={money(estimate)}>
          {estimatePct != null && <div>{estimatePct}% of target</div>}
          {year.days_left_in_plan > 0 && <div>At the pace of the last {trendDays} days</div>}
        </Figure>
        {gap != null && (
          <Figure
            label={gap < 0 ? "Short of target" : "Over target"}
            value={`${gap < 0 ? "−" : "+"}${money(Math.abs(gap))}`}
            tone={gap < 0 ? "warning" : "success"}
          />
        )}
      </div>
      <div className="px-4 pb-2 pt-4">
        <YearProgressChart currency={currency} months={year.months} />
      </div>
    </Card>
  )
}
